<template>
    <main-layout>
        <inertia-head>
            <title>Contact Us</title>
        </inertia-head>

        <template #header>
            <h2 class="font-bold text-xl text-white leading-tight">
                Contact Us
            </h2>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm">
                    <div class="p-6 bg-white border-b border-gray-200">
                        <div class="font-medium text-xl text-secondary mb-2">
                            Contact Information
                        </div>
                        <div class="text-sm text-gray-500 mb-10">
                            You can contact us by sending text messages, phone
                            or email.<br />
                            For all general inquiries, please email us at
                            <span :class="linkClasses">
                                <a href="mailto:exports@birchwood.ca">
                                    exports@birchwood.ca
                                </a>
                            </span>
                            .
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 lg:gap-12">
                            <div
                                class="mb-10"
                                v-for="rep in reps"
                                :key="rep.id"
                            >
                                <div
                                    class="
                                        text-gray-500
                                        tracking-wide
                                        font-medium
                                        border-b
                                        mb-4
                                    "
                                >
                                    {{ locationNames(rep.locations) }}
                                </div>

                                <div class="flex flex-wrap lg:flex-nowrap">
                                    <!-- name -->
                                    <div
                                        class="
                                            lg:flex-shrink
                                            text-secondary
                                            font-semibold
                                            text-lg
                                            tracking-wide
                                            mb-2
                                            pr-16
                                        "
                                    >
                                        {{ rep.firstname }} {{ rep.lastname }}
                                    </div>

                                    <div
                                        class="text-sm tracking-wide leading-7"
                                    >
                                        <!-- phone -->
                                        <div class="flex">
                                            <div
                                                class="
                                                    text-gray-600
                                                    font-medium
                                                    w-20
                                                "
                                            >
                                                Phone
                                            </div>
                                            <div :class="linkClasses">
                                                <a
                                                    :href="
                                                        'tel:+1-' +
                                                        formatPhone(rep.phone)
                                                    "
                                                >
                                                    {{ formatPhone(rep.phone) }}
                                                </a>
                                            </div>
                                        </div>

                                        <!-- email -->
                                        <div class="flex">
                                            <div
                                                class="
                                                    text-gray-600
                                                    font-medium
                                                    w-20
                                                "
                                            >
                                                Email
                                            </div>
                                            <div :class="linkClasses">
                                                <span
                                                    v-html="rep.emailLink"
                                                ></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="pb-12">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm">
                    <div class="p-6 bg-white border-b border-gray-200">
                        <div class="font-medium text-xl text-secondary mb-2">
                            Have some questions?
                        </div>
                        <div class="text-sm text-gray-500 mb-10">
                            Please fill out our form, and we’ll get in touch
                            shortly.
                        </div>

                        <form @submit.prevent="submit">
                            <div
                                class="font-medium text-secondary tracking-wide"
                            >
                                Your Information
                            </div>
                            <div
                                class="
                                    grid grid-cols-1
                                    lg:grid-cols-3 lg:gap-4
                                    mb-8
                                "
                            >
                                <!-- first name -->
                                <div class="mt-4">
                                    <message-input-component
                                        id="firstname"
                                        type="text"
                                        class="w-full"
                                        v-model="form.firstname"
                                        placeholder="Your First Name"
                                        required
                                        autocomplete="firstname"
                                    />
                                </div>

                                <!-- last name -->
                                <div class="mt-4">
                                    <message-input-component
                                        id="lastname"
                                        type="text"
                                        class="w-full"
                                        v-model="form.lastname"
                                        placeholder="Your Last Name"
                                        required
                                        autocomplete="lastname"
                                    />
                                </div>

                                <!-- email -->
                                <div class="mt-4">
                                    <message-input-component
                                        id="email"
                                        type="email"
                                        class="w-full"
                                        v-model="form.email"
                                        placeholder="Your Email Address"
                                        required
                                        autocomplete="email"
                                    />
                                </div>
                            </div>

                            <div
                                class="
                                    font-medium
                                    text-secondary
                                    tracking-wide
                                    mb-4
                                "
                            >
                                Message
                            </div>
                            <div class="mb-8">
                                <textarea-component
                                    id="message"
                                    class="w-full h-28"
                                    v-model="form.message"
                                    placeholder="Type your message here"
                                    required
                                />
                            </div>

                            <div
                                class="
                                    flex flex-wrap
                                    lg:flex-nowrap
                                    justify-end
                                    mt-4
                                "
                            >
                                <div
                                    v-if="status"
                                    class="
                                        mb-4
                                        lg:mb-0
                                        text-secondary
                                        font-medium
                                        text-sm
                                        animate-pulse
                                        flex
                                        justify-center
                                        items-center
                                        mx-8
                                    "
                                >
                                    {{ status }}
                                </div>
                                <button
                                    type="submit"
                                    class="
                                        inline-flex
                                        bg-primary
                                        rounded-full
                                        text-sm text-white
                                        px-10
                                        py-2
                                        font-medium
                                        tracking-wide
                                        focus:outline-none focus:bg-secondary
                                        hover:bg-secondary
                                        transition-all
                                        duration-500
                                    "
                                    :class="{ 'opacity-25': form.processing }"
                                    :disabled="form.processing"
                                >
                                    Send Message
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </main-layout>
</template>

<script>
import MainLayout from "@/Layouts/Main";
import MessageInputComponent from "@/Components/MessageInput";
import TextareaComponent from "@/Components/Textarea";

export default {
    components: {
        MainLayout,
        MessageInputComponent,
        TextareaComponent,
    },
    props: {
        reps: {
            type: Array,
            default: null,
        },
        status: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            linkClasses: [
                "cursor-pointer",
                "text-primary",
                "hover:text-primary-light",
            ],
            form: this.$inertia.form({
                firstname: "",
                lastname: "",
                email: "",
                message: "",
            }),
        };
    },
    methods: {
        locationNames(locations) {
            let result = "";
            for (let i in locations) {
                result += locations[i].location;
                if (i < locations.length - 2) {
                    result += ", ";
                } else if (i == locations.length - 2) {
                    result += " & ";
                }
            }
            return result;
        },
        formatPhone(value) {
            let result = value.toString();
            if (result.length === 10) {
                result = result.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
            }
            return result;
        },
        submit() {
            this.form.post(this.route("contact.message"), {
                preserveState: false,
                preserveScroll: true,
                onSuccess: () => this.form.reset(),
            });
        },
    },
};
</script>
